import type { CourseAssignment, CourseModule, CourseRun, CourseRunInput, CourseSession } from "../../data/course";

export type BuilderStep = "details" | "schedule" | "curriculum" | "review";

export function newCourseId(prefix: string) {
    const random = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replaceAll("-", "").slice(0, 10)
        : Math.random().toString(36).slice(2, 12);
    return `${prefix}-${random}`;
}

const future = (days: number, hour: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    date.setHours(hour, 0, 0, 0);
    return date.toISOString();
};

export function blankAssignment(): CourseAssignment {
    return { id: newCourseId("assignment"), title: "", description: "", required: true };
}

export function blankSession(): CourseSession {
    return {
        id: newCourseId("session"), title: "", type: "LIVE", startsAt: future(7, 10), endsAt: future(7, 11),
        joinUrl: "", recordingUrl: "", venue: "", required: true, cancelled: false, assignments: [],
    };
}

export function blankModule(): CourseModule {
    return { id: newCourseId("module"), title: "", label: "", required: true, sessions: [blankSession()] };
}

export function blankCourse(): CourseRunInput {
    return {
        schemaVersion: 1, courseTitle: "", courseType: "COURSE", programTrackKey: "", programTrackLabel: "", programTier: 1, description: "", learningOutcomes: [""], imageUrl: "", coverImageUrl: "", cardImageUrl: "",
        runTitle: "", startAt: future(7, 9), endAt: future(35, 18), timezone: "Asia/Kolkata",
        minimumAttendancePercent: 75, enrollmentMode: "PAID", priceINR: 1, publicationStatus: "draft", modules: [blankModule()],
    };
}

export function courseToInput(course: CourseRun): CourseRunInput {
    return {
        schemaVersion: 1, programId: course.programId, courseTitle: course.courseTitle, courseType: course.courseType,
        programTrackKey: course.programTrackKey || "", programTrackLabel: course.programTrackLabel || "", programTier: course.programTier,
        description: course.description || "", learningOutcomes: course.learningOutcomes || [], imageUrl: course.imageUrl || "", coverImageUrl: course.coverImageUrl || course.imageUrl || "", cardImageUrl: course.cardImageUrl || course.imageUrl || "",
        runTitle: course.runTitle, startAt: course.startAt, endAt: course.endAt, timezone: course.timezone,
        minimumAttendancePercent: course.minimumAttendancePercent, enrollmentMode: course.enrollmentMode,
        ...(course.enrollmentMode === "PAID" ? { priceINR: course.priceINR } : {}), ...(course.capacity ? { capacity: course.capacity } : {}),
        publicationStatus: course.courseStatus === "draft" ? "draft" : "published", modules: course.modules,
    };
}

export function repeatCourse(course: CourseRun): CourseRunInput {
    const input = courseToInput(course);
    const nextStart = new Date(future(7, 9));
    const previousStart = new Date(course.startAt);
    const shift = nextStart.getTime() - previousStart.getTime();
    const move = (value: string) => new Date(new Date(value).getTime() + shift).toISOString();
    return {
        ...input,
        runTitle: `${course.courseTitle} — ${nextStart.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}`,
        startAt: nextStart.toISOString(),
        endAt: move(course.endAt),
        publicationStatus: "draft",
        modules: input.modules.map((module) => ({ ...module, sessions: module.sessions.map((session) => ({ ...session, startsAt: move(session.startsAt), endsAt: move(session.endsAt), joinUrl: "", recordingUrl: "", cancelled: false })) })),
    };
}

export function dateTimeLocalValue(value: string, timeZone: string) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(value));
    const part = (type: string) => parts.find((item) => item.type === type)?.value || "";
    return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

export function zonedDateTimeIso(value: string, timeZone: string) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
    if (!match) return value;
    const [, year, month, day, hour, minute] = match;
    const target = Date.UTC(+year, +month - 1, +day, +hour, +minute);
    let guess = target;
    for (let index = 0; index < 2; index += 1) {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
        }).formatToParts(new Date(guess));
        const part = (type: string) => +(parts.find((item) => item.type === type)?.value || 0);
        const represented = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
        guess += target - represented;
    }
    return new Date(guess).toISOString();
}

export function moveItem<T>(items: T[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length) return items;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
}
